import { createRouter, createWebHistory } from "vue-router";

import { getAxiosInstance } from "@/axiosClient";
import authService from "@/services/auth.service";
import { useUserAccessesStore } from "@/stores/user-access";
import { userHasAnyRole } from "@/utils/userRoles";

let isImpersonating = false;

// Rutas
const routes = [
  {
    path: "/impersonate/:username",
    name: "impersonate",
    beforeEnter: async (to, from, next) => {
      if (isImpersonating) {
        return next(false);
      }
      isImpersonating = true;
      const username = to.params.username;

      try {
        const axios = getAxiosInstance();
        const response = await axios.get(`/impersonate?username=${username}`);

        const newToken = response.data.token;
        if (newToken) {
          localStorage.setItem(
            "originalToken",
            localStorage.getItem("token") || "",
          );
          localStorage.setItem("token", newToken);
          next("/");
          setTimeout(() => location.reload(), 500);
        }
      } catch (error) {
        console.error("Error impersonating:", error);
        next(false);
      }
    },
  },
  {
    path: "/switchBack",
    name: "switchBack",
    beforeEnter: (to, from, next) => {
      const originalToken = localStorage.getItem("originalToken");
      if (originalToken) {
        localStorage.setItem("token", originalToken);
        localStorage.removeItem("originalToken");
      }
      next("/");
      setTimeout(() => location.reload(), 500);
    },
  },
  {
    path: "/login",
    name: "login",
    meta: { title: "BASF Mi Cuenta - Login" },
    component: () => import("@/views/Login.vue"),
  },
  {
    path: "/verify-2fa",
    name: "verify-2fa",
    meta: { title: "BASF Mi Cuenta - Verificar 2FA" },
    component: () => import("@/views/Verify2FA.vue"),
  },
  {
    path: "/",
    name: "landing",
    meta: {
      title: "BASF Mi Cuenta",
      requiresAuth: true,
    },
    component: () => import("@/views/Home.vue"),
  },
  {
    path: "/permisos",
    name: "permisos",
    meta: {
      title: "BASF Mi Cuenta - Permisos",
      requiresAuth: true,
      hasAnyRole: ["Performance"],
    },
    component: () => import("@/views/Permisos.vue"),
  },
  {
    path: "/password/:url",
    name: "password",
    meta: { title: "BASF Mi Cuenta - Password" },
    component: () => import("@/views/Password.vue"),
  },
  {
    path: "/excel",
    name: "excel",
    meta: {
      title: "BASF Mi Cuenta - Excel",
      requiresAuth: true,
      hasAnyRole: ["ADMIN"],
    },
    component: () => import("@/views/Excel.vue"),
  },
  {
    path: "/metricas",
    name: "metricas",
    meta: {
      title: "BASF Mi Cuenta - Métricas",
      requiresAuth: true,
      hasAnyRole: ["ADMIN", "Performance"],
    },
    component: () => import("@/views/Metricas.vue"),
  },
  {
    path: "/seleccionarCuenta",
    name: "seleccionarCuenta",
    meta: {
      title: "BASF Mi Cuenta - Seleccionar Cuenta",
      requiresAuth: true,
    },
    component: () => import("@/views/SeleccionarCuenta.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("@/views/NotFound.vue"),
  },
];

// Crear router
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// Guard global
router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  if (!requiresAuth) {
    next();
    return;
  }

  const user = await authService.getCurrentUser();
  const hasRequiredRole = user && userHasAnyRole(user, to.meta.hasAnyRole);

  if (hasRequiredRole) {
    const userAccessesStore = useUserAccessesStore();
    await userAccessesStore.getUserPermissions();
    next();
  } else {
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("unauthorized"));
    }, 500);
  }
});

export default router;
